import React, { Component } from "react";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import Initial from "./forms/Initial";
import Company from "./forms/Company";
import Plan from "./forms/Plan";
import MaximumUsers from "./forms/MaximumUsers";
import Pending from "./forms/Pending";
import Review from "./forms/Review";
import Success from "./forms/Success";
import BusinessDashboard from "./forms/BusinessDashboard";
import ActivateCheckout from "./forms/Activate";
import {
    Toast, LottieLoadingIndicator, PortalPageShell
} from "src/components";
import { SubscriptionContext } from "./SubscriptionContext";
import SubscriptionCostResponse from "src/scenes/Subscription/api/SubscriptionCostResponse";

import { UserContext } from "src/scenes/App/UserContext";
import * as PERMISSIONS from "src/constants/permissions";
import { SubscriptionMode, SubscriptionPaymentMode, UserKeys } from "src/constants/userDetails";
import { withRouter } from "react-router-dom";
import ActivateBusinessActionPrompt from "src/scenes/Setting/components/ActivateBusinessActionPrompt";
import dayjs from "dayjs";

export const Forms = {
    INITIAL: 0,
    COMPANY_NAME: 1,
    PLAN: 2,
    REVIEW: 3,
    SUCCESS: 4,
    ACTIVE: 5,
    PURCHASE: 6,
    CHECKOUT: 7,
    ACTIVATE_CHECKOUT: 7.5,
    PAYMENT_SUCCESS: 8,
    MAX_USER: 9,
    SUBSCRIPTION_PENDING: 10
};

const StorageKeys = {
    TEMP_COMPANY_NAME: "tempCompanyName",
    TEMP_COMPANY_ABN: "tempAbn",
    TOTAL_NUMBER: "totalNumber",
    MORE_USERS: "moreUsers",
    IS_AWAITING_ACTIVATION: "isAwaitingActivation"
};

class Subscription extends Component {
    static contextType = UserContext;
    
    state = {
        loadingCount: 0,
        currentForm: this.context.companyName !== null ? Forms.ACTIVE : Forms.INITIAL,
        isNextClickable: this.context.companyName !== null,
        acceptToc: false,
        companyName: this.context.companyName ? this.context.companyName : "",
        abn: "",
        website: "",
        country: "",
        // to maintain value through Initial component re-mounts, eg. when revisiting the Initial screen/form
        isValidCountry: false,
        promoCode: "",
        totalNumber: 0,
        paymentMode: undefined,
        timezone: "",
        quoteSendTime: "",
        method: "paypal",
        costing: SubscriptionCostResponse.default(),
        employees: 0,
        clients: 0,
        trialClientUsers: 0,
        isSalesAndPromotionsEnabled: false,
        moreUsers: 1,
        // We create moment library objects from our start date and end date
        //    to enable easier date comparison (see usage of the .diff method)
        isCancelled: false,
        loadingStatus: false,
        hideResult: true,
        resultMessage: "",
        isPaymentFailing: true,
        awaitingActivation: false,
        hasBillingPermission: false,
        isPurchasingMoreUsers: false,
        isSubscriptionExpired: false,
        companyMode: null,
        activateBusinessPrompt: false,
        showActivateBusinessActionPrompt: true,
        activateBusinessPayment: false,
        licenseTier: null,
        isActive: false,
        hasActivatedTier: false,
    };

    /**
     * Re-show (after was already shown) the dialog to pay the initial subscription.
     * Will still only be shown if the subscription is active but has never been paid.
     */
    presentActivateBusinessPrompt() {
        this.setState({
            showActivateBusinessActionPrompt: true
        });
    }

    async getCompanyRegistrationStatus() {
        this.setState((prevState) => {
            return { loadingCount: prevState.loadingCount + 1 };
        });
        try {
            let response = await axios.get(`${API_URL}/companyregistration`, {
                headers: {
                    Authorization: "Bearer " + this.context.token
                }
            });
            if (response && response.data.company_registration.awaitingActivation) {
                this.setState({
                    currentForm: Forms.SUBSCRIPTION_PENDING,
                    awaitingActivation: true,
                });
                localStorage.setItem(StorageKeys.IS_AWAITING_ACTIVATION, response.data.company_registration.awaitingActivation);
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.setState((prevState) => {
                return { loadingCount: prevState.loadingCount - 1 };
            });
        }
    }
    
    //we just provide a return url from PayPal and this local storage will help us to determine which
    //page the front-end should display to the user correctly.
    componentDidMount() {
        const userContext = this.context;
        if (this.context) {
            this.setState({
                licenseTier: userContext[UserKeys.LICENSE_TIER]
            });

            const subscriptionRequired = userContext[UserKeys.LICENSE_TIER]?.shortTermDuration === 0 && userContext[UserKeys.LICENSE_TIER]?.freeForever !== true;
            // User created on business portal without license tier
            if (!userContext[UserKeys.LICENSE_TIER] && userContext[UserKeys.IS_CLIENT]) {
                console.log("Fetching tier for client not on a license tier...")
                this.getCompanyLicenseTier();
                return;
            }

            // User created on app or web with license tier but not purchased yet
            if (subscriptionRequired && userContext[UserKeys.IS_CLIENT]) {
                console.log("Fetching tier for client requiring a subscription...");
                console.log(userContext[UserKeys.LICENSE_TIER]);
                this.getCompanyLicenseTier();
                return;
            }

            if (userContext[UserKeys.IS_CUSTOMIZE_COMPLETE]) {
                // triggering before company setup is completed will return unexpected argument error in the api due to no employee record and company to retrieve
                this.getCompanySubscriptionStatus(userContext.token);
            }

            /** check if user has Billing permission
             * this will prevent 403s when user doesn't have billing permission */
            const hasBillingPermission = PERMISSIONS.hasPermissions([PERMISSIONS.BILLING], userContext.permissions);
            this.setState({
                hasBillingPermission: hasBillingPermission
            }, () => {
                //check if user has existing subscription request (for subscribers with more than 1000 users)
                if (!userContext.isActive) {
                    if (Object.prototype.hasOwnProperty.call(localStorage, StorageKeys.IS_AWAITING_ACTIVATION)) {
                        this.setState({ currentForm: Forms.SUBSCRIPTION_PENDING });
                    } else if (userContext.companyName === null) {
                        this.getCompanyRegistrationStatus();
                    }
                }

                this.loadDetails(hasBillingPermission);

                if (!userContext.isActive) {
                    // isCustomComplete being false means current user has not yet finished setting up their company, thus not an expired one
                    if (userContext[UserKeys.IS_CUSTOMIZE_COMPLETE]) {
                        this.setState({ currentForm: Forms.ACTIVE });
                    }
                    else {
                        // The payment cannot fail if the user
                        // account is not even active yet
                        this.setState({
                            isPaymentFailing: false
                        });
                    }
                }
            });
        }
    }

    getCompanyLicenseTier = async () => {
        try {
            const res = await axios.get(`${API_URL}/appDetails`, {
                headers: {
                    Authorization: "Bearer " + this.context.token
                }
            });

            const data = res.data;
            const license = data?.clientLicense;
            const tiers = license?.currentTerms?.tiers || [];

            if (tiers.length === 1 && !this.state.hasActivatedTier) {
                console.log("Activating tier " + tiers[0].uuid);
                this.setState({ hasActivatedTier: true });
                await this.activateClientLicense(tiers[0].uuid);
                return this.getCompanyLicenseTier();
            }
            
            if (tiers.length > 1) {
                this.setState({
                    showTierSelectionModal: true,
                    availableTiers: tiers,
                    renewalPeriod: license?.currentTerms?.renewalPeriod,
                    shortTermDuration: license?.currentTerms?.shortTermDuration,
                    currentTerms: license,
                });
            }

            return data;

        } catch (err) {
            console.error("Failed to fetch appDetails", err);
            return null;
        }
    };

    activateClientLicense = async (tierUuid) => {
        try {
            await axios.post(`${API_URL}/activateClientLicense`, {
                tierUuid
            }, {
                headers: {
                    Authorization: "Bearer " + this.context.token
                }
            });
            return true;
          
        } catch (err) {
            console.error("Activation failed", err);
            Toast.error(err.getApiErrorDetails().message);
        }
    };

    getCompanySubscriptionStatus = async () => {
        await axios.get(`${API_URL}/company`, {
            headers: {
                Authorization: "Bearer " + this.context.token
            }
        }).then(res => {

            if (res.data.mode === SubscriptionMode.DEMO) {
                this.setState({ isSubscriptionExpired: false });
                return;
            }

            const currentSub = res.data.currentSubscription;
            if (!currentSub || currentSub.periodsDue > 0) {
                this.setState({ isSubscriptionExpired: true });
                return;
            }

            const num = Number(currentSub.durationNumber);
            const unit = (currentSub.durationUnit).toLowerCase();
            const subStart = currentSub.startAt;

            const subEnd = dayjs(subStart).add(num, unit);
            const withGrace = this.applyGrace(subEnd.toDate(), unit);

            const isExpired = Date.now() >= withGrace.valueOf();
            this.setState({ isSubscriptionExpired: isExpired });
        }).catch(err => {
            return Toast.error(err.getApiErrorDetails().message);
        });
    };

    applyGrace = (subEnd, unit) => {
        const date = new Date(subEnd.valueOf());

        if (unit === "hour") {
            date.setHours(date.getHours() + 1);
            date.setMinutes(0, 0, 0);
        } else {
            date.setDate(date.getDate() + 1);
            date.setHours(0, 0, 0, 0);
        }

        return date;
    };

    loadDetails = async hasBillingPermission => {
        if (hasBillingPermission) {
            await this.loadSubscriptionCost();
        }
        await this.getActiveCompanyDetails();
    };

    getActiveCompanyDetails = async () => {
        this.setState((prevState) => {
            return { loadingCount: prevState.loadingCount + 1 };
        });

        try {
            // Get the pricing metrics from the API
            let costing = this.state.costing;
            if (this.state.hasBillingPermission) {
                costing = await SubscriptionCostResponse.get(this.context.token);
            }

            const res = await axios.get(`${API_URL}/company`, {
                headers: {
                    Authorization: "Bearer " + this.context.token
                }
            });

            let totalNumber = res.data.totalNumber;
            let paymentMode = res.data.currentSubscription?.paymentPeriod === 1 ?
                SubscriptionPaymentMode.PaymentPlan :
                SubscriptionPaymentMode.Upfront;
            let employees = res.data.employees;
            let clients = res.data.clients;
            let trialClientUsers = res.data.trialClientUsers;
            let isSalesAndPromotionsEnabled = res.data.isSalesAndPromotionsEnabled;
            let companyName = res.data.companyName;
            const abn = res.data.abn;
            const countryCode = res.data.countryCode;
            let isCancelled = res.data.isCancelled;
            let isPaymentFailing = res.data.isPaymentFailing;

            const companyMode = String(res.data.mode || "").trim().toUpperCase();

            //redirection comes from employee list `add more users`
            if (JSON.parse(localStorage.getItem("redirect"))) {
                localStorage.removeItem("redirect");
                this.setState({
                    currentForm: Forms.PURCHASE
                });
            } else {
                await this.context.appFns.reinitialiseUser();

                const subscriptionRequired = this.context[UserKeys.SUBSCRIPTION_MODE] === SubscriptionMode.ACTIVE;
                const subscriptionWasEverPaid = Boolean(this.context[UserKeys.SUBSCRIPTION_WAS_EVER_PAID]);
                const needsActivation = subscriptionRequired && !subscriptionWasEverPaid;

                this.setState((prevState) => {
                    return {
                        ...prevState,
                        currentForm: Forms.ACTIVE,
                        companyName: companyName,
                        abn: abn,
                        country: countryCode,
                        totalNumber: totalNumber,
                        paymentMode: paymentMode,
                        employees: employees,
                        clients: clients,
                        trialClientUsers: trialClientUsers,
                        isSalesAndPromotionsEnabled: isSalesAndPromotionsEnabled,
                        isNextClickable: true,
                        costing: costing,
                        currentSubscription: res.data.currentSubscription || null,
                        isCancelled: isCancelled,
                        isPaymentFailing: isPaymentFailing,
                        companyMode,
                        activateBusinessPrompt: needsActivation,
                    };
                });
            }

            //release these values since they are stored in state
            localStorage.removeItem(StorageKeys.TOTAL_NUMBER);
            localStorage.removeItem(StorageKeys.MORE_USERS);
        } catch (error) {
            this.setState({
                isNextClickable: true,
            });
            return Toast.error(error.getApiErrorDetails().message);
        } finally {
            this.setState((prevState) => {
                return { loadingCount: prevState.loadingCount - 1 };
            });
        }
    };

    loadSubscriptionCost = async () => {
        this.setState((prevState) => {
            return { loadingCount: prevState.loadingCount + 1 };
        });
        try {
            const costing = await SubscriptionCostResponse.get(this.context.token);
            this.setState({
                costing: costing
            });
        } catch {
            console.log("Failed loading subscription cost");
        } finally {
            this.setState((prevState) => {
                return { loadingCount: prevState.loadingCount - 1 };
            });
        }
    };

    reloadCosting = (costing) => {
        this.setState({ costing });
    };

    nextClick = () => {
        if (this.state.isNextClickable) {
            if (this.state.currentForm === Forms.PLAN && this.state.totalNumber > 1000) {
                this.setState({
                    currentForm: Forms.MAX_USER,
                    hideResult: true,
                });
            } else if (this.state.currentForm === Forms.REVIEW) {
                this.context.appFns.updateUserContextProperties({ [UserKeys.IS_ACTIVE]: true });
                this.setState({
                    currentForm: Forms.SUCCESS,
                    hideResult: true,
                });
            } else if (this.state.currentForm === Forms.PAYMENT_SUCCESS) {
                this.setState({
                    currentForm: Forms.ACTIVE,
                    hideResult: true
                });
            } else if (this.state.currentForm === Forms.SUCCESS) {
                window.location.href = "/customise";
            } else if (this.state.currentForm === Forms.CHECKOUT) {
                let totalNumber = parseInt(this.state.totalNumber) + parseInt(this.state.moreUsers);
                this.setState({
                    totalNumber: totalNumber,
                    hideResult: true,
                });
                this.setState({
                    currentForm: Forms.PAYMENT_SUCCESS,
                    hideResult: true,
                });
            } else if (this.state.currentForm === Forms.ACTIVE) {
                // isPurchasingMore users on true will let COMPANY_NAME and PLAN differentiate between setup and purchase more users
                this.setState({
                    isPurchasingMoreUsers: true,
                    currentForm: Forms.COMPANY_NAME
                });
            } else if (this.state.currentForm === Forms.COMPANY_NAME && this.state.isPurchasingMoreUsers) {
                this.setState({ currentForm: Forms.PURCHASE });
            } else if (this.state.currentForm === Forms.ACTIVATE_CHECKOUT) {
                this.setState({
                    currentForm: Forms.SUCCESS,
                    hideResult: true
                });
            }
            else {
                const newForm = this.state.currentForm + 1;
                this.setState({
                    currentForm: newForm,
                    hideResult: true,
                });
            }
        }
    };

    goToPurchaseMoreUsers = () => {
        const { currentSubscription, moreUsers } = this.state;
        if (!currentSubscription) {
            Toast.error("Your current subscription could not be ascertained.");
            return;
        }

        this.setState({
            isPurchasingMoreUsers: true,
            currentForm: Forms.PURCHASE,
            moreUsers: moreUsers || 1
        });
    };

    updateValue = (name, value) => {
        this.setState((prevState) => {
            return {
                ...prevState,
                isNextClickable: true,
                [name]: value,
            };
        });
    };

    updateCompanyValueAndPossiblyGoNext = (name, value, goNext) => {
        this.setState((prevState) => {
            return {
                ...prevState,
                isNextClickable: true,
                [name]: value,
            };
        }, () => goNext && this.nextClick());
    };

    updatePrice = (currentForm, value) => {
        if (currentForm === Forms.PLAN) {
            this.setState((prevState) => {
                return {
                    ...prevState,
                    totalNumber: value
                };
            });
        } else {
            this.setState((prevState) => {
                return {
                    ...prevState,
                    moreUsers: value
                };
            });
        }
    };

    backToInitial = (form) => {
        const newForm = form === Forms.PLAN || form === Forms.REVIEW ? Forms.INITIAL : Forms.ACTIVE;
        this.setState({
            ...this.state,
            currentForm: newForm,
        });
    };

    backToPrevious = () => {
        // show "Active" tab when clicking back on Company when purchasing more users
        if (this.state.currentForm === Forms.COMPANY_NAME && this.state.isPurchasingMoreUsers) {
            return this.setState({
                currentForm: Forms.ACTIVE,
                isPurchasingMoreUsers: false
            });
        }
        if (this.state.currentForm === Forms.ACTIVATE_CHECKOUT) {
            return this.setState({
                currentForm: Forms.ACTIVE
            });
        }
        const newForm = this.state.currentForm === Forms.MAX_USER ? 2 : this.state.currentForm - 1;
        this.setState({
            ...this.state,
            currentForm: newForm,
        });
    };

    onPaymentMethodSaved = () => {
        this.setState({
            currentForm: Forms.ACTIVE
        });
    };

    sendSubscriptionRequest = (fullName, emailAddress, mobileNumber, businessName, message) => {
        const { uuid, totalNumber } = this.state;
        axios.post(`${API_URL}/companyregistration`, {
            "totalNumber": totalNumber,
            "uuid": uuid,
        }, {
            headers: {
                Authorization: "Bearer " + this.context.token
            }
        }).then(_ => {
            // Send subscription request email
            axios.post(`${API_URL}/businesssubscription/sendmail`, {
                "fullName": fullName, 
                "emailAddress": emailAddress, 
                "mobileNumber": mobileNumber,
                "businessName": businessName, 
                "message": message
            }, {
                headers: {
                    Authorization: "Bearer " + this.context.token
                }
            }).then(_ => {
                this.setState({
                    currentForm: Forms.SUBSCRIPTION_PENDING,
                    awaitingActivation: true
                });
                localStorage.setItem(StorageKeys.IS_AWAITING_ACTIVATION, true);
            }).catch(error => {
                this.showError(error.getApiErrorDetails().message);
            });
        }).catch(error => {
            this.showError(error.getApiErrorDetails().message);
        });
    };

    showError = (error) => {
        this.setState({
            resultMessage: error
        });
        return Toast.error(error);
    };

    setUserPermissions = (permissionIds) => {
        // Update the UserContext property
        this.context.permissions = JSON.parse(permissionIds);
    };

    overviewDefaultValues = (done) => {
        const s = this.state;
        const hasPrimaryPlan = s.costing && s.costing.primaryPlan();

        if (!hasPrimaryPlan) {
            setTimeout(() => this.overviewDefaultValues(done), 50);
            return;
        }

        const primary = s.costing.primaryPlan();
        const updates = {};

        if (!s.totalNumber || s.totalNumber < 1) updates.totalNumber = 1;
        if (!s.currencyCode) updates.currencyCode = primary.currency;
        if (!s.paymentMode) {
            updates.paymentMode = primary.paymentPeriod === 1 ?
                SubscriptionPaymentMode.PaymentPlan :
                SubscriptionPaymentMode.Upfront;
        }

        if (Object.keys(updates).length) this.setState(updates, () => done && done());
        else if (done) done();
    };

    render() {
        const userContext = this.context;
        const { loadingCount, currentForm, acceptToc, companyName, abn, website, country, isValidCountry, promoCode, totalNumber, paymentMode,
            currentSubscription, companyMode, isCancelled, hideResult,
            costing, method, employees, clients, trialClientUsers, isSalesAndPromotionsEnabled, loadingStatus, resultMessage,
            moreUsers, isSubscriptionExpired,
            isPaymentFailing, isPurchasingMoreUsers } = this.state;

        if (loadingCount > 0 && currentForm !== Forms.ACTIVE) {
            return <LottieLoadingIndicator />;
        }

        const subscriptionRequired = companyMode === SubscriptionMode.ACTIVE;
        const subscriptionWasEverPaid = Boolean(this.context[UserKeys.SUBSCRIPTION_WAS_EVER_PAID]);
        const showActivateBusinessActionPrompt = subscriptionRequired && !subscriptionWasEverPaid && this.state.showActivateBusinessActionPrompt;
        const useFixedPurchaseFormLayout = [
            Forms.PLAN,
            Forms.REVIEW,
            Forms.PURCHASE,
            Forms.CHECKOUT,
            Forms.ACTIVATE_CHECKOUT
        ].includes(currentForm);

        return (
            <PortalPageShell
                height={useFixedPurchaseFormLayout ? "calc(100vh - 64px - 1rem)" : "auto"}
                overflow={useFixedPurchaseFormLayout ? "hidden" : "visible"}
                paddingBottom={useFixedPurchaseFormLayout ? "0" : undefined}
            >
                <SubscriptionContext.Provider value={{
                    updateValue: this.updateValue,
                    updateCompanyValueAndPossiblyGoNext: this.updateCompanyValueAndPossiblyGoNext,
                    sendSubscriptionRequest: this.sendSubscriptionRequest,
                    acceptToc: acceptToc,
                    nextClick: this.nextClick,
                    goToPurchaseMoreUsers: this.goToPurchaseMoreUsers,
                    companyName: companyName,
                    abn: abn,
                    website: website,
                    country: country,
                    paymentMode: paymentMode,
                    isValidCountry: isValidCountry,
                    promoCode: promoCode,
                    finishSubmission: this.finishSubmission,
                    updatePrice: this.updatePrice,
                    totalNumber: totalNumber,
                    backToInitial: this.backToInitial,
                    backToPrevious: this.backToPrevious,
                    costing: costing,
                    reloadCosting: this.reloadCosting,
                    currentForm: currentForm,
                    moreUsers: moreUsers,
                    setUserPermissions: this.setUserPermissions,
                    method: method,
                    currentSubscription: currentSubscription,
                    isActive: userContext.isActive,
                    employees: employees,
                    clients: clients,
                    trialClientUsers: trialClientUsers,
                    isSalesAndPromotionsEnabled: isSalesAndPromotionsEnabled,
                    email: userContext.email,
                    token: userContext.token,
                    isCancelled: isCancelled,
                    onPaymentMethodSaved: this.onPaymentMethodSaved,
                    loadingStatus: loadingStatus,
                    resultMessage: resultMessage,
                    hideResult: hideResult,
                    isPaymentFailing: isPaymentFailing,
                    isPurchasingMoreUsers: isPurchasingMoreUsers,
                    isSubscriptionExpired: isSubscriptionExpired,
                    loadingCount: loadingCount,
                    companyMode: this.state.companyMode,
                    activateBusinessPayment: this.state.activateBusinessPayment,
                    isCustomizeComplete: userContext.isCustomizeComplete
                }}>

                    <ActivateBusinessActionPrompt
                        showDialog={showActivateBusinessActionPrompt}
                        // Disabled the close to encourage users to pay
                        // handleClose={() => this.setState({ showActivateBusinessActionPrompt: false })}
                        confirmCallback={() => {
                            this.setState({ showActivateBusinessActionPrompt: false, activateBusinessPayment: true },
                                () => {
                                    this.updateValue("currentForm", Forms.ACTIVATE_CHECKOUT);
                                });
                        }}
                        companyDetails={{ companyName: this.state.companyName }}
                        actionName="Proceed"
                        lowercaseAction="proceed"
                    />

                    { currentForm === Forms.INITIAL &&
                        <Initial /> }
                    { currentForm === Forms.COMPANY_NAME &&
                        <Company /> }
                    { currentForm === Forms.PLAN &&
                        <Plan /> }
                    { currentForm === Forms.MAX_USER &&
                        <MaximumUsers /> }
                    { currentForm === Forms.SUBSCRIPTION_PENDING &&
                        <Pending /> }
                    { currentForm === Forms.REVIEW &&
                        <Review /> }
                    { currentForm === Forms.SUCCESS &&
                        <Success /> }
                    { currentForm === Forms.ACTIVE &&
                        <BusinessDashboard 
                            presentActivateBusinessPrompt={() => this.presentActivateBusinessPrompt()} 
                            licenseTier={this.state.licenseTier}
                            showTierSelectionModal={this.state.showTierSelectionModal}
                            availableTiers={this.state.availableTiers}
                            onSelectTier={(tierUuid) => this.activateClientLicense(tierUuid)}
                            renewalPeriod={this.state.renewalPeriod}
                            shortTermDuration={this.state?.shortTermDuration}
                            currentTerms={this.state.currentTerms}
                        /> }
                    { currentForm === Forms.PURCHASE &&
                        <Plan /> }
                    { currentForm === Forms.CHECKOUT &&
                        <Review /> }
                    { currentForm === Forms.PAYMENT_SUCCESS &&
                        <Success /> }
                    { currentForm === Forms.ACTIVATE_CHECKOUT && (
                        <ActivateCheckout
                            nextClick={() => this.updateValue("currentForm", Forms.SUCCESS)}
                        />
                    )}
                </SubscriptionContext.Provider>
            </PortalPageShell>
        );
    }
}

export default withRouter(Subscription);
